import { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Radio,
    IconButton,
    Chip,
    Tooltip
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import linkedinIcon from "../../Components/Assets/images/linkedinIcon.png";
import indeedIcon from "../../Components/Assets/images/indeedIcon.png";
import naukriIcon from "../../Components/Assets/images/naukriIcon.png";
import monsterIcon from "../../Components/Assets/images/monsterIcon.png";
import glassdoorIcon from "../../Components/Assets/images/glassdoorIcon.png";
import founditIcon from "../../Components/Assets/images/funditIcon.png";

// ---- JSON data for platforms (icons from assets) ----
const platforms = [
    { id: "linkedin", label: "LinkedIn", icon: linkedinIcon },
    { id: "indeed", label: "Indeed", icon: indeedIcon },
    { id: "naukri", label: "Naukri", icon: naukriIcon },
    { id: "monster", label: "Monster", icon: monsterIcon },
    { id: "glassdoor", label: "Glassdoor", icon: glassdoorIcon },
    { id: "foundit", label: "Foundit", icon: founditIcon }
];

// ---- Mock search result data ----
const mockProfiles = [
    {
        id: 1,
        name: "Amit Sharma",
        contact: "amit@email.com",
        experience: 5,
        languages: ["JavaScript", "React", "Node"]
    },
    {
        id: 2,
        name: "Neha Verma",
        contact: "neha@email.com",
        experience: 3,
        languages: ["Java", "Spring", "SQL"]
    },
    {
        id: 3,
        name: "Rahul Mehta",
        contact: "rahul@email.com",
        experience: 7,
        languages: ["Python", "Django", "AWS"]
    },
    {
        id: 4,
        name: "Anita Singh",
        contact: "anita@email.com",
        experience: 6,
        languages: ["JavaScript", "Angular", "TypeScript"]
    },
    {
        id: 5,
        name: "Suresh Kumar",
        contact: "suresh@email.com",
        experience: 4,
        languages: ["JavaScript", "React"]
    },
    {
        id: 6,
        name: "Pooja Patel",
        contact: "pooja@email.com",
        experience: 2,
        languages: ["HTML", "CSS", "JavaScript"]
    },
    {
        id: 7,
        name: "Vikram Joshi",
        contact: "vikram@email.com",
        experience: 8,
        languages: ["Java", "Spring", "Microservices"]
    },
    {
        id: 8,
        name: "Kiran Rao",
        contact: "kiran@email.com",
        experience: 5,
        languages: ["Python", "Flask", "SQL"]
    },
    {
        id: 9,
        name: "Deepak Mishra",
        contact: "deepak@email.com",
        experience: 10,
        languages: ["AWS", "DevOps", "Docker"]
    },
    {
        id: 10,
        name: "Sneha Iyer",
        contact: "sneha@email.com",
        experience: 6,
        languages: ["React", "Redux", "TypeScript"]
    },
    {
        id: 11,
        name: "Arjun Nair",
        contact: "arjun@email.com",
        experience: 3,
        languages: ["Node", "Express", "MongoDB"]
    },
    {
        id: 12,
        name: "Meera Kulkarni",
        contact: "meera@email.com",
        experience: 7,
        languages: ["Python", "FastAPI", "PostgreSQL"]
    },
    {
        id: 13,
        name: "Rohit Saxena",
        contact: "rohit@email.com",
        experience: 5,
        languages: ["Angular", "TypeScript"]
    },
    {
        id: 14,
        name: "Nidhi Jain",
        contact: "nidhi@email.com",
        experience: 9,
        languages: ["Java", "Spring Boot", "Hibernate"]
    },
    {
        id: 15,
        name: "Manish Gupta",
        contact: "manish@email.com",
        experience: 4,
        languages: ["React", "Next.js"]
    },
    {
        id: 16,
        name: "Ritu Malhotra",
        contact: "ritu@email.com",
        experience: 6,
        languages: ["JavaScript", "Vue", "Pinia"]
    },
    {
        id: 17,
        name: "Sanjay Yadav",
        contact: "sanjay@email.com",
        experience: 11,
        languages: ["Python", "Machine Learning"]
    },
    {
        id: 18,
        name: "Ayesha Khan",
        contact: "ayesha@email.com",
        experience: 3,
        languages: ["React", "Tailwind"]
    },
    {
        id: 19,
        name: "Prakash Menon",
        contact: "prakash@email.com",
        experience: 8,
        languages: ["AWS", "Node", "Serverless"]
    },
    {
        id: 20,
        name: "Divya Choudhary",
        contact: "divya@email.com",
        experience: 2,
        languages: ["JavaScript", "Bootstrap"]
    }
];


export default function ProfileSearchPage() {
    const [selectedPlatform, setSelectedPlatform] = useState("linkedin");
    const [query, setQuery] = useState("");
    const [minExperience, setMinExperience] = useState(""); // ✅ ADD THIS
    const [results, setResults] = useState([]);
    const [favourites, setFavourites] = useState([]);
    // ---- Load favourites from localStorage ----
    useEffect(() => {
        const fav = JSON.parse(localStorage.getItem("favourites")) || [];
        setFavourites(fav);
    }, []);

    // ---- Persist favourites ----
    useEffect(() => {
        localStorage.setItem("favourites", JSON.stringify(favourites));
    }, [favourites]);

    const handleSearch = () => {
        const filtered = mockProfiles.filter((p) => {
            // name OR language match
            const matchQuery =
                p.name.toLowerCase().includes(query.toLowerCase()) ||
                p.languages.some((lang) =>
                    lang.toLowerCase().includes(query.toLowerCase())
                );

            // experience match
            const matchExperience = minExperience
                ? p.experience >= Number(minExperience)
                : true;

            return matchQuery && matchExperience;
        });

        setResults(filtered);
    };

    const toggleFavourite = (id) => {
        setFavourites((prev) =>
            prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, width: "100%", mx: "auto" }}>
            <Typography variant="h5" fontWeight={600} mb={3}>
                Profile Search
            </Typography>

            {/* Platform Selection */}
            <Grid container spacing={2} mb={3}>
                {platforms.map((platform) => (
                    <Grid item xs={6} sm={4} md={2} key={platform.id}>
                        <Tooltip title={platform.label} arrow placement="top">
                            <Box
                                onClick={() => setSelectedPlatform(platform.id)}
                                sx={{
                                    border: "1px solid",
                                    borderColor:
                                        selectedPlatform === platform.id
                                            ? "primary.main"
                                            : "grey.300",
                                    borderRadius: 2,
                                    p: 1.5,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    boxShadow: selectedPlatform === platform.id ? 2 : 0
                                }}
                            >
                                <Radio checked={selectedPlatform === platform.id} />
                                <img src={platform.icon} alt={platform.label} height={24} />
                                {/* <Typography variant="body2" fontWeight={500}>
                {platform.label}
              </Typography> */}
                            </Box>
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>

            {/* Search Bar */}
            {/* <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={9}>
                    <TextField
                        fullWidth
                        placeholder="Search by name"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                </Grid>
            </Grid> */}
            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} md={5}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or language"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        type="number"
                        placeholder="Minimum experience (years)"
                        value={minExperience}
                        onChange={(e) => setMinExperience(e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} md={3}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                </Grid>
            </Grid>

            {/* Result Grid */}
            {results.length === 0 ? (
                <Typography textAlign="center" color="text.secondary">
                    No profiles found
                </Typography>
            ) : (
                <Grid container spacing={3}>
                    {results.map((profile) => (
                        <Grid item xs={12} sm={6} md={4} key={profile.id}>
                            <Card sx={{ borderRadius: 3, height: "100%" }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between">
                                        <Box>
                                            <Typography variant="h6">{profile.name}</Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                {profile.contact}
                                            </Typography>

                                            <Typography variant="body2" mt={1}>
                                                Experience: {profile.experience} Years
                                            </Typography>

                                            {/* Languages */}
                                            <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                                                {profile.languages.map((lang) => (
                                                    <Chip key={lang} size="small" label={lang} variant="outlined" />
                                                ))}
                                            </Box>

                                            {/* Platform */}
                                            <Chip
                                                size="small"
                                                label={selectedPlatform}
                                                sx={{ mt: 1, textTransform: "capitalize" }}
                                            />
                                        </Box>

                                        <IconButton onClick={() => toggleFavourite(profile.id)}>
                                            {favourites.includes(profile.id) ? (
                                                <FavoriteIcon color="error" />
                                            ) : (
                                                <FavoriteBorderIcon />
                                            )}
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

        </Box >
    );
}
